import { useWithPreMiddleWare } from '@/api/useWithPreMiddleWare';
import {
  BaseAPI,
  FeatureFlagsApi,
  LenderManagementLenderBranchesApi,
  LenderManagementLenderBranchProductsApi,
  LenderManagementLenderHQApi,
  MasterOrdersApi,
  ProductCategoriesApi,
  ProductGroupsApi,
  ProductsApi,
  VendorManagementVendorHQApi,
  VendorManagementVendorProductsApi,
  VendorNotificationsManagementControllerApi,
} from '@xpanse/organization-service-client';

import { useApiConfig } from './useApiConfig';

const useOrgApiConfig = () => useApiConfig(import.meta.env.VITE_ORGANIZATION_SERVICE_BASE_PATH);

export const useLenderManagementApi = () =>
  useWithPreMiddleWare(new LenderManagementLenderHQApi(useOrgApiConfig()));

export const useBranchesApi = () =>
  useWithPreMiddleWare(new LenderManagementLenderBranchesApi(useOrgApiConfig()));

export const useProductsApi = () => useWithPreMiddleWare(new ProductsApi(useOrgApiConfig()));

export const useLenderBranchProductsApi = () =>
  useWithPreMiddleWare(new LenderManagementLenderBranchProductsApi(useOrgApiConfig()));

export const useVendorApi = () =>
  useWithPreMiddleWare(new VendorManagementVendorHQApi(useOrgApiConfig()));

export const useVendorProductsApi = () =>
  useWithPreMiddleWare(new VendorManagementVendorProductsApi(useOrgApiConfig()));

export const useProductCategoriesApi = () =>
  useWithPreMiddleWare(new ProductCategoriesApi(useOrgApiConfig()));

export const useVendorNotificationsManagementApi = () =>
  useWithPreMiddleWare(new VendorNotificationsManagementControllerApi(useOrgApiConfig()));

export const useMasterOrdersApi = () =>
  useWithPreMiddleWare(new MasterOrdersApi(useOrgApiConfig()));

export const useProductGroupsApi = () => {
  const config = useOrgApiConfig();
  const api = useWithPreMiddleWare(new ProductGroupsApi(config));
  return api;
};

export const useFeatureFlagsApi = () =>
  useWithPreMiddleWare(new FeatureFlagsApi(useOrgApiConfig()));

/**
 * `GET /productcategories/event-types?categoryType=…` is not in the organization-service
 * OpenAPI spec yet, so `ProductCategoriesApi` has no generated method for it. Extending
 * the generated `BaseAPI` reuses the runtime the rest of the clients use — tenant-aware
 * base path, `fetch-override`, and the Bearer token attached by `useWithPreMiddleWare`.
 * Once the endpoint ships in the spec, run `yarn generate` and move callers onto
 * `ProductCategoriesApi`.
 */
export class ProductCategoryEventTypesApi extends BaseAPI {
  /** Raw payload — shaped by `normalizeOrderEventTypes` in `useOrderEventTypes`. */
  async listEventTypes(categoryType: string): Promise<unknown> {
    const response = await this.request({
      path: '/productcategories/event-types',
      method: 'GET',
      headers: {},
      query: { categoryType },
    });

    const body = await response.text();
    return body ? JSON.parse(body) : [];
  }

  /**
   * Advanced Filter event types: categories enabled by the `event-type-searchable-filter`
   * flag return only their searchable events.
   */
  async listSearchableEventTypes(categoryType: string): Promise<unknown> {
    const response = await this.request({
      path: '/productcategories/searchable-event-types',
      method: 'GET',
      headers: {},
      query: { categoryType },
    });

    const body = await response.text();
    return body ? JSON.parse(body) : [];
  }

  async listEventTypesWithDictionary(categoryType?: string): Promise<unknown> {
    const response = await this.request({
      path: '/productcategories/event-types-with-dictionary',
      method: 'GET',
      headers: {},
      query: categoryType ? { categoryType } : {},
    });
    const body = await response.text();
    return body ? JSON.parse(body) : [];
  }
}

export interface AttachmentDocumentTypeOption {
  value?: string;
  label?: string;
}

export class ProductAttachmentDocumentTypesApi extends BaseAPI {
  async listAttachmentDocumentTypes(tags: string[] = ['FMC']): Promise<AttachmentDocumentTypeOption[]> {
    const response = await this.request({
      path: '/products/attachment-document-types',
      method: 'GET',
      headers: {},
      query: { tags },
    });

    const body = await response.text();
    return body ? (JSON.parse(body) as AttachmentDocumentTypeOption[]) : [];
  }
}

export const useProductCategoryEventTypesApi = () =>
  useWithPreMiddleWare(new ProductCategoryEventTypesApi(useOrgApiConfig()));

export const useProductAttachmentDocumentTypesApi = () =>
  useWithPreMiddleWare(new ProductAttachmentDocumentTypesApi(useOrgApiConfig()));

export interface TenantPodiumFeatureToggle {
  name?: string;
  description?: string;
  isEnabled?: boolean;
}

export interface AdminTenantPodiumConfigResponse {
  stored?: Record<string, TenantPodiumFeatureToggle> | null;
  effective?: Record<string, boolean> | null;
  updatedAt?: string;
  updatedBy?: string;
}

export class TenantPodiumConfigAdminApi extends BaseAPI {
  async getPodiumConfig(): Promise<AdminTenantPodiumConfigResponse> {
    const response = await this.request({
      path: '/admin/tenant-config/podium',
      method: 'GET',
      headers: {},
    });
    const body = await response.text();
    return body ? (JSON.parse(body) as AdminTenantPodiumConfigResponse) : {};
  }

  /** PATCH body is a flat map of featureKey → toggle fields to merge. */
  async patchPodiumConfig(
    patch: Record<string, TenantPodiumFeatureToggle>
  ): Promise<AdminTenantPodiumConfigResponse> {
    const response = await this.request({
      path: '/admin/tenant-config/podium',
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: patch,
    });
    const body = await response.text();
    return body ? (JSON.parse(body) as AdminTenantPodiumConfigResponse) : {};
  }
}

export const useTenantPodiumConfigAdminApi = () =>
  useWithPreMiddleWare(new TenantPodiumConfigAdminApi(useOrgApiConfig()));
